import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import { container } from '@/app/container';
import { errorToastId } from '@/shared/request-feedback';
import { FilesRepositoryToken } from '../../di/tokens';
import type { StoredFileFilters } from '../../domain/entities/stored-file.entity';
import { describeFileError } from '../format';

/** Prefixo de lista, summary e detalhe: toda mutação mexe nos três. */
export const FILES_KEY = ['files'];
export const fileDetailKey = (fileId: string) => [
  ...FILES_KEY,
  'detail',
  fileId,
];

export function useStoredFiles(
  page: number,
  perPage: number,
  filters: StoredFileFilters,
) {
  const repository = container.resolve(FilesRepositoryToken);
  return useQuery({
    queryKey: [...FILES_KEY, 'list', page, perPage, filters],
    queryFn: () => repository.list({ page, perPage, filters }),
    placeholderData: keepPreviousData,
  });
}

export function useFilesSummary(filters: StoredFileFilters) {
  const repository = container.resolve(FilesRepositoryToken);
  return useQuery({
    queryKey: [...FILES_KEY, 'summary', filters],
    queryFn: () => repository.summary(filters),
    placeholderData: keepPreviousData,
  });
}

export function useStoredFile(fileId: string) {
  const repository = container.resolve(FilesRepositoryToken);
  return useQuery({
    queryKey: fileDetailKey(fileId),
    queryFn: () => repository.get(fileId),
  });
}

/**
 * Troca o texto do toast global (a mensagem crua do back) pelo do código, no
 * mesmo id: um aviso só, e sem o texto cru.
 */
function toastFileError(err: unknown, fallback: string) {
  toast.error(describeFileError(err, fallback), { id: errorToastId(err) });
}

export function useForceDeleteFile(fileId: string) {
  const repository = container.resolve(FilesRepositoryToken);
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [...FILES_KEY, 'force-delete', fileId],
    mutationFn: (reason: string) => repository.forceDelete(fileId, reason),
    onError: (err) => toastFileError(err, 'O arquivo não foi apagado.'),
    // Também no erro: o 409 é hold ligado por outra pessoa, e o 503 deixa o
    // estado do objeto incerto. Nos dois o detalhe relido é o que vale.
    onSettled: () =>
      void queryClient.invalidateQueries({ queryKey: FILES_KEY }),
  });
}

/**
 * Mutation, não query: cada clique pede uma URL nova, e o back audita quem
 * abriu. Uma query em cache pediria a URL sem ninguém ter clicado.
 */
export function useFileViewUrl(fileId: string) {
  const repository = container.resolve(FilesRepositoryToken);
  return useMutation({
    mutationKey: [...FILES_KEY, 'view-url', fileId],
    mutationFn: () => repository.viewUrl(fileId),
    onError: (err) => toastFileError(err, 'Não foi possível abrir o arquivo.'),
  });
}

/** Sem update otimista: o hold no cache só muda com a resposta do back. */
export function useSetLegalHold(fileId: string) {
  const repository = container.resolve(FilesRepositoryToken);
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [...FILES_KEY, 'legal-hold', fileId],
    mutationFn: (input: { legalHold: boolean; reason?: string }) =>
      repository.setLegalHold(fileId, input),
    onError: (err) => toastFileError(err, 'O legal hold não foi alterado.'),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: FILES_KEY }),
  });
}
